import { useMemo } from 'react';
import type { ColumnDef } from '@deckspace/types';
import { ICON_REGISTRY } from '../../components/surface/content/icon-registry';

interface TimelineViewProps {
  nodeId: string;
  mapping: {
    date: string;
    label: string;
    description?: string;
    icon?: string;
  };
  orientation?: 'horizontal' | 'vertical';
  // Injected by withDataset HOC (Task 8):
  rows: Record<string, unknown>[];
  columnDefs?: ColumnDef[];
}

export function TimelineView({ mapping, orientation = 'vertical', rows }: TimelineViewProps) {
  const events = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => ({
        date: String(row[mapping.date] ?? ''),
        label: String(row[mapping.label] ?? ''),
        description: mapping.description ? String(row[mapping.description] ?? '') : undefined,
        icon: mapping.icon ? String(row[mapping.icon] ?? '') : undefined,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [rows, mapping.date, mapping.label, mapping.description, mapping.icon]);

  if (orientation === 'horizontal') {
    return (
      <div className="flex items-start gap-4 overflow-x-auto py-4">
        {events.map((evt, i) => (
          <div key={i} className="flex min-w-[8rem] flex-col items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-blue-500" />
            <span className="text-xs text-zinc-500">{evt.date}</span>
            <span className="text-center text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {evt.label}
            </span>
            {evt.description && (
              <span className="text-center text-xs text-zinc-400">{evt.description}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {events.map((evt, i) => {
        const IconComponent = evt.icon ? ICON_REGISTRY[evt.icon] : null;
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                {IconComponent ? (
                  <IconComponent className="h-3 w-3 text-blue-600" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                )}
              </div>
              {i < events.length - 1 && (
                <div className="w-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
              )}
            </div>
            <div className="pb-4">
              <span className="text-xs text-zinc-500">{evt.date}</span>
              <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {evt.label}
              </div>
              {evt.description && <div className="text-xs text-zinc-400">{evt.description}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
